import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle, FileText, ArrowRightLeft } from 'lucide-react';
import { MarketplaceDeal } from '@/hooks/useMarketplaceDeals';
import { format } from 'date-fns';
import { ProfessionalDealOverview } from '@/components/deals/ProfessionalDealOverview';
import { CounterOfferThread } from './CounterOfferThread';
import { CounterOfferModal } from './CounterOfferModal';
import { useCounterOffers } from '@/hooks/useCounterOffers';
import { useUser } from '@/contexts/UserContext';

interface MarketplaceDealDetailModalProps {
  deal: MarketplaceDeal | null;
  isOpen: boolean;
  onClose: () => void;
  onRefresh?: () => void;
}

export const MarketplaceDealDetailModal: React.FC<MarketplaceDealDetailModalProps> = ({
  deal,
  isOpen,
  onClose,
  onRefresh,
}) => {
  const [showCounterModal, setShowCounterModal] = useState(false);
  const { currentUser, accountType } = useUser();
  const {
    counterOffers,
    loading,
    fetchCounterOffers,
    respondToCounterOffer,
  } = useCounterOffers();

  useEffect(() => {
    if (isOpen && deal?.user_application_id) {
      fetchCounterOffers(deal.user_application_id);
    }
  }, [isOpen, deal?.user_application_id, fetchCounterOffers]);

  if (!deal) return null;

  const hasApplication = !!deal.user_application_id;
  const canNegotiate = hasApplication && deal.user_application_status === 'pending';

  const handleAccept = async (counterId: string) => {
    if (!deal.user_application_id) return;
    const result = await respondToCounterOffer(counterId, 'accepted', deal.user_application_id);
    if (result.success) {
      onRefresh?.();
      onClose();
    }
  };

  const handleReject = async (counterId: string) => {
    if (!deal.user_application_id) return;
    await respondToCounterOffer(counterId, 'rejected', deal.user_application_id);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Marketplace Deal Overview
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="overview" className="w-full mt-4">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              {hasApplication && (
                <TabsTrigger value="negotiations" className="flex items-center gap-1">
                  <ArrowRightLeft className="h-3 w-3" />
                  Negotiations
                  {counterOffers.some(co => co.status === 'pending' && co.proposed_by_profile_id !== currentUser?.id) && (
                    <Badge variant="destructive" className="ml-1 h-4 px-1 text-xs">New</Badge>
                  )}
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="overview" className="mt-6">
              <ProfessionalDealOverview
                deal={deal}
                primaryParty={{
                  name: deal.brand_name || "Brand Partner",
                  type: "brand",
                  details: "Marketing & Partnerships Division"
                }}
                secondaryParty={{
                  name: "Open Position",
                  type: "talent",
                  details: "Professional Partnership Opportunity"
                }}
                dealType={deal.deal_type || "sponsorship"}
              >
                <div className="space-y-4">
                  <div>
                    <div className="font-semibold text-foreground mb-2">Opportunity Details:</div>
                    <div className="text-muted-foreground">
                      <div>• Open marketplace opportunity for qualified athletes</div>
                      <div>• Competitive application process</div>
                      {deal.deadline && (
                        <div>• Application Deadline: {format(new Date(deal.deadline), 'MMMM dd, yyyy')}</div>
                      )}
                      <div>• Performance-based partnership structure</div>
                    </div>
                  </div>

                  {deal.requirements && deal.requirements.length > 0 && (
                    <div>
                      <div className="font-semibold text-foreground mb-2">Requirements:</div>
                      <div className="text-muted-foreground space-y-1">
                        {deal.requirements.map((req, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <span>{req}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="font-semibold text-foreground mb-2">Partnership Benefits:</div>
                    <div className="text-muted-foreground">
                      <div>• Brand association and co-marketing opportunities</div>
                      <div>• Professional development and exposure</div>
                      <div>• Performance incentives and bonus structures</div>
                      <div>• Long-term partnership potential based on results</div>
                    </div>
                  </div>
                </div>
              </ProfessionalDealOverview>
            </TabsContent>

            {hasApplication && (
              <TabsContent value="negotiations" className="mt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Negotiation Thread</h3>
                  {canNegotiate && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowCounterModal(true)}
                      className="flex items-center gap-2"
                    >
                      <ArrowRightLeft className="h-4 w-4" />
                      Propose Terms
                    </Button>
                  )}
                </div>
                <CounterOfferThread
                  counterOffers={counterOffers}
                  onAccept={handleAccept}
                  onReject={handleReject}
                  onCounter={() => setShowCounterModal(true)}
                  loading={loading}
                />
              </TabsContent>
            )}
          </Tabs>
        </DialogContent>
      </Dialog>

      {deal.user_application_id && (
        <CounterOfferModal
          isOpen={showCounterModal}
          onClose={() => setShowCounterModal(false)}
          dealApplicationId={deal.user_application_id}
          dealId={deal.id}
          currentValue={deal.deal_value}
          currentDealType={deal.deal_type}
          currentDeadline={deal.deadline}
          onSuccess={() => {
            fetchCounterOffers(deal.user_application_id!);
            onRefresh?.();
          }}
        />
      )}
    </>
  );
};
